/**
 * Inline furigana (ruby text) syntax parser for Japanese learning content.
 *
 * Syntax: `[base]{reading}` → ruby annotation
 *   "これは[漢字]{かんじ}です" → text "これは" + ruby(漢字/かんじ) + text "です"
 *
 * Invalid forms (`[漢字`, `[漢字]{}`, `[]{かんじ}`) never throw — they fall
 * back to plain text rendering (empty base/reading segments are dropped).
 */

export type FuriganaSegment =
  | { type: "text"; value: string }
  | { type: "ruby"; base: string; reading: string };

const FURIGANA_RE = /\[(.*?)\]\{(.*?)\}/g;

export const parseFurigana = (text: string | null | undefined): FuriganaSegment[] => {
  if (!text) return [];

  const segments: FuriganaSegment[] = [];
  let lastIndex = 0;

  // Reset stateful regex (global flag retains lastIndex across calls)
  FURIGANA_RE.lastIndex = 0;

  let match: RegExpExecArray | null;
  while ((match = FURIGANA_RE.exec(text)) !== null) {
    const [whole, base, reading] = match;
    const start = match.index;

    if (start > lastIndex) {
      segments.push({ type: "text", value: text.slice(lastIndex, start) });
    }

    if (base && reading) {
      segments.push({ type: "ruby", base, reading });
    } else if (base) {
      // `[base]{}` → keep base as plain text, drop empty reading wrapper
      segments.push({ type: "text", value: base });
    }
    // `[]{reading}` and `[]{}` are dropped entirely

    lastIndex = start + whole.length;
  }

  if (lastIndex < text.length) {
    segments.push({ type: "text", value: text.slice(lastIndex) });
  }

  return segments;
};
